import { cn } from "../../lib/utils.ts";

/**
 * The app's loading indicator, the in-game app's own (design-system/Spinner,
 * client 2026-11-03): a twelve-spoke throbber in Modern Football blue,
 * optionally naming what it is waiting on. Every spinner in the practice
 * apps is this one, at its spot's size.
 *
 * Spokes are one brand blue at descending OPACITY rather than twelve literal
 * hexes from the palette. A fixed ramp can only be tuned for one ground —
 * the pale end disappears on white, the dark end disappears on the near-black
 * boot screen — whereas opacity composites correctly over both, and over
 * white it lands on the palette tints anyway.
 *
 * The same markup is inlined into the dashboard's index.html as the
 * pre-mount boot screen, and its `.mofo-spinner` turn is in globals.css. Keep
 * the three in step.
 */

const SPOKE_COUNT = 12;
const SPOKES = Array.from({ length: SPOKE_COUNT }, (_, i) => i);

export const SPINNER_BLUE = "#0066ff";

export const SpinnerMark = ({
  size = 56,
  color = SPINNER_BLUE,
  className,
}: {
  size?: number;
  /** White on a blue button; red in an error note. Brand blue elsewhere. */
  color?: string;
  className?: string;
}) => (
  <span
    aria-hidden
    className={cn("mofo-spinner relative inline-block shrink-0", className)}
    style={{ width: size, height: size }}
  >
    {SPOKES.map((i) => (
      <span
        key={i}
        className="absolute left-1/2 top-0 block rounded-full"
        style={{
          width: size * 0.1,
          height: size * 0.26,
          marginLeft: size * -0.05,
          background: color,
          // Brightest spoke leads, the rest trail off behind it — the
          // comet that reads as direction of travel.
          opacity: 1 - (i / SPOKE_COUNT) * 0.88,
          transform: `rotate(${i * (360 / SPOKE_COUNT)}deg)`,
          transformOrigin: `50% ${size / 2}px`,
        }}
      />
    ))}
  </span>
);

export const Spinner = ({
  label,
  size,
  fullScreen = false,
  className,
}: {
  /** What the app is waiting on, e.g. "Loading the app". Omitted = no text. */
  label?: string;
  size?: number;
  /**
   * The app's own boot surface (white, fixed over the viewport), like the
   * index.html boot screen — for the whole screen; transparent and filling
   * its positioned parent otherwise.
   */
  fullScreen?: boolean;
  className?: string;
}) => (
  <div
    className={cn(
      "flex h-full w-full flex-col items-center justify-center gap-4",
      fullScreen ? "fixed inset-0 bg-white text-gray-900" : "absolute bg-none",
      className
    )}
    role="status"
    aria-live="polite"
    aria-label={label ?? "Loading"}
  >
    <SpinnerMark size={size} />
    {label ? (
      <p className="text-sm font-medium tracking-wide text-current opacity-70">{label}</p>
    ) : null}
  </div>
);
